import { Injectable } from '@angular/core';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { SessionService } from '../session/session.service';
import {
  ICalendarSettings,
  createDefaultSettings,
} from '../../_interfaces/ICalendarSettings';

/**
 * Clave base para localStorage
 */
const STORAGE_KEY_PREFIX = 'crm_calendar_settings';

/**
 * Servicio para gestionar la persistencia de las configuraciones del calendario.
 *
 * Actualmente usa localStorage como almacenamiento provisional.
 * Está preparado para migrar a un servicio REST en el futuro.
 *
 * TODO: Migrar a API REST
 * - Cambiar saveToStorage() por llamada POST/PUT a /api/users/{userId}/calendar-settings
 * - Cambiar loadFromStorage() por llamada GET a /api/users/{userId}/calendar-settings
 * - Añadir HttpClient como dependencia
 * - Manejar errores de red con fallback a localStorage (offline mode)
 */
@Injectable({
  providedIn: 'root',
})
export class CalendarSettingsService {
  /**
   * BehaviorSubject para mantener el estado actual y notificar cambios
   */
  private settingsSubject = new BehaviorSubject<ICalendarSettings | null>(null);

  /**
   * Observable público para suscribirse a cambios en la configuración
   */
  public settings$ = this.settingsSubject.asObservable();

  /**
   * Cache de la configuración actual
   */
  private currentSettings: ICalendarSettings | null = null;

  /**
   * Flag para saber si ya se cargó la configuración
   */
  private loaded = false;

  constructor(private sessionService: SessionService) {}

  /**
   * Obtiene la clave de almacenamiento para el usuario actual
   */
  private getStorageKey(): string {
    const userId = this.sessionService.getUserID() || 'anonymous';
    const customerId = this.sessionService.getCustomer()?.id || 'no-customer';
    const siteId = this.sessionService.getSite()?.id || 'no-site';
    return `${STORAGE_KEY_PREFIX}_${userId}_${customerId}_${siteId}`;
  }

  /**
   * Guarda la configuración del calendario.
   *
   * @param settings - Configuración a guardar
   * @returns Observable que emite la configuración guardada
   *
   * TODO: Reemplazar por llamada HTTP
   * return this.http.put<ICalendarSettings>(
   *   `/api/users/${userId}/calendar-settings`,
   *   settings
   * ).pipe(tap(saved => this.updateCache(saved)));
   */
  saveSettings(settings: ICalendarSettings): Observable<ICalendarSettings> {
    return this.saveToStorage(settings).pipe(
      tap((saved) => this.updateCache(saved)),
    );
  }

  /**
   * Obtiene la configuración del calendario.
   * Si no existe, retorna la configuración por defecto.
   *
   * @param forceReload - Si es true, fuerza la recarga desde el almacenamiento
   * @returns Observable con la configuración
   *
   * TODO: Reemplazar por llamada HTTP
   * return this.http.get<ICalendarSettings>(
   *   `/api/users/${userId}/calendar-settings`
   * ).pipe(
   *   catchError(() => of(createDefaultSettings())),
   *   tap(settings => this.updateCache(settings))
   * );
   */
  getSettings(forceReload = false): Observable<ICalendarSettings> {
    // Si ya tenemos la configuración en cache y no forzamos recarga
    if (this.loaded && !forceReload && this.currentSettings) {
      return of(this.currentSettings);
    }

    return this.loadFromStorage().pipe(
      tap((settings) => {
        this.updateCache(settings);
        this.loaded = true;
      }),
    );
  }

  /**
   * Obtiene la configuración de forma síncrona (solo si ya está cargada)
   * Útil para inicialización rápida
   */
  getSettingsSync(): ICalendarSettings {
    return this.currentSettings || createDefaultSettings();
  }

  /**
   * Resetea la configuración a los valores por defecto
   *
   * @returns Observable con la configuración por defecto
   */
  resetSettings(): Observable<ICalendarSettings> {
    const defaultSettings = createDefaultSettings();
    return this.saveSettings(defaultSettings);
  }

  /**
   * Actualiza una propiedad específica de la configuración
   *
   * @param path - Ruta de la propiedad (ej: 'view.showSundays')
   * @param value - Nuevo valor
   * @returns Observable con la configuración actualizada
   */
  updateSetting<T>(path: string, value: T): Observable<ICalendarSettings> {
    const settings = this.getSettingsSync();
    const updated = this.setNestedProperty(settings, path, value);
    return this.saveSettings(updated);
  }

  /**
   * Limpia la configuración del usuario actual del almacenamiento
   */
  clearSettings(): void {
    try {
      localStorage.removeItem(this.getStorageKey());
      this.currentSettings = null;
      this.loaded = false;
      this.settingsSubject.next(null);
    } catch (error) {
      console.error('Error clearing calendar settings:', error);
    }
  }

  // ============================================================================
  // MÉTODOS PRIVADOS - Almacenamiento (a reemplazar por HTTP en el futuro)
  // ============================================================================

  /**
   * Guarda en localStorage (provisional)
   *
   * @param settings - Configuración a guardar
   * @returns Observable que emite la configuración guardada
   */
  private saveToStorage(
    settings: ICalendarSettings,
  ): Observable<ICalendarSettings> {
    try {
      const key = this.getStorageKey();
      const data = JSON.stringify(settings);
      localStorage.setItem(key, data);

      // Simular respuesta async como si fuera HTTP
      return of(settings);
    } catch (error) {
      console.error('Error saving calendar settings:', error);
      // En caso de error, aún así retornamos los settings
      return of(settings);
    }
  }

  /**
   * Carga desde localStorage (provisional)
   *
   * @returns Observable con la configuración o valores por defecto
   */
  private loadFromStorage(): Observable<ICalendarSettings> {
    try {
      const key = this.getStorageKey();
      const data = localStorage.getItem(key);

      if (data) {
        const parsed = JSON.parse(data) as Partial<ICalendarSettings>;
        // Merge con defaults para asegurar que nuevas propiedades existan
        const settings = this.mergeWithDefaults(parsed);
        return of(settings);
      }

      return of(createDefaultSettings());
    } catch (error) {
      console.error('Error loading calendar settings:', error);
      return of(createDefaultSettings());
    }
  }

  /**
   * Actualiza el cache interno y notifica a los suscriptores
   */
  private updateCache(settings: ICalendarSettings): void {
    this.currentSettings = settings;
    this.settingsSubject.next(settings);
  }

  /**
   * Mezcla la configuración guardada con los valores por defecto
   * Esto asegura que nuevas propiedades añadidas tengan valor
   */
  private mergeWithDefaults(
    saved: Partial<ICalendarSettings>,
  ): ICalendarSettings {
    const defaults = createDefaultSettings();

    return {
      view: { ...defaults.view, ...saved.view },
      display: { ...defaults.display, ...saved.display },
      filters: saved.filters?.length
        ? this.mergeFilters(defaults.filters, saved.filters)
        : defaults.filters,
    };
  }

  /**
   * Mezcla los filtros guardados con los por defecto
   * Mantiene el estado 'active' de los filtros guardados
   */
  private mergeFilters(
    defaultFilters: ICalendarSettings['filters'],
    savedFilters: ICalendarSettings['filters'],
  ): ICalendarSettings['filters'] {
    return defaultFilters.map((defaultFilter) => {
      const savedFilter = savedFilters.find((f) => f.id === defaultFilter.id);
      return savedFilter
        ? { ...defaultFilter, active: savedFilter.active }
        : defaultFilter;
    });
  }

  /**
   * Establece una propiedad anidada en un objeto
   */
  private setNestedProperty<T>(obj: T, path: string, value: any): T {
    const clone = JSON.parse(JSON.stringify(obj)) as T;
    const keys = path.split('.');
    let current: any = clone;

    for (let i = 0; i < keys.length - 1; i++) {
      current = current[keys[i]];
    }

    current[keys[keys.length - 1]] = value;
    return clone;
  }
}
